import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { HubConnectionBuilder, HttpTransportType, LogLevel } from "@microsoft/signalr";
import { agencySessionKey, subscribeAgencySession } from "../../utils/agencySession.ts";
import AppFeedback from "../../components/shared/AppFeedback.tsx";
import { markNotificationsRead, readNotifications, type NotificationRecord } from "./notificationApi.ts";
import { t } from "../store-language/i18n.ts";
import { notificationText } from "./notificationHtml.ts";
import notificationChime from "./notification-chime.wav";

export function useNotificationInbox() {
  const session = useSyncExternalStore(subscribeAgencySession, agencySessionKey);
  const [state, setState] = useState<{ session: string | null; records: NotificationRecord[] }>({ session: null, records: [] });
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState<number | "all" | null>(null);
  const [live, setLive] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const controller = useRef<AbortController>();
  const mutationLock = useRef(false);
  const liveRef = useRef(false);
  const failed = useRef(false);
  const initialized = useRef(false);
  const highestId = useRef(0);
  const refreshedAt = useRef(0);

  const refresh = useCallback(async function () {
    if (!session || session !== agencySessionKey()) return;
    controller.current?.abort();
    const request = new AbortController();
    controller.current = request;
    setLoading(true);
    try {
      const records = await readNotifications(request.signal);
      if (request.signal.aborted || session !== agencySessionKey()) return;
      failed.current = false;
      const latest = records[0];
      if (initialized.current && latest && latest.id > highestId.current) {
        AppFeedback.notify(latest.title, notificationText(latest.message), "info");
        const audio = new Audio(notificationChime);
        void audio.play().catch(function () { /* Browser autoplay policy; in-app notification remains available. */ });
        if ("Notification" in window && Notification.permission === "granted") {
          try {
            const popup = new Notification(latest.title, { body: notificationText(latest.message), tag: "portal-notification", silent: true });
            popup.onclick = function () { popup.close(); window.focus(); };
          } catch { /* Some mobile browsers require a service-worker notification. */ }
        }
      }
      initialized.current = true;
      highestId.current = latest?.id || 0;
      refreshedAt.current = Date.now();
      setState({ session, records });
    } catch (error) {
      if (!request.signal.aborted && session === agencySessionKey()) {
        failed.current = true;
        setState({ session, records: [] });
        void AppFeedback.httpError(error);
      }
    } finally {
      if (!request.signal.aborted && session === agencySessionKey()) setLoading(false);
    }
  }, [session]);

  useEffect(function () {
    setState({ session, records: [] });
    failed.current = false;
    initialized.current = false;
    highestId.current = 0;
    setPending(null);
    mutationLock.current = false;
    if (!session) { setLoading(false); return; }
    void refresh();
    return function () { controller.current?.abort(); };
  }, [session, refresh]);

  useEffect(function () {
    if (!session) return;
    let active = true;
    const connection = new HubConnectionBuilder()
      .withUrl(`${(process.env.REACT_APP_Server_Url || "").replace(/\/+$/, "")}/catalog/notifications/hub`, {
        withCredentials: true, transport: HttpTransportType.LongPolling,
      })
      .configureLogging(LogLevel.None)
      .build();
    // No automatic retries, including 5xx. Refresh explicitly restarts the connection.
    connection.on("notificationsChanged", function () { if (active && !failed.current) void refresh(); });
    connection.onclose(function () {
      liveRef.current = false;
      if (active) setLive(false);
    });
    void connection.start().then(function () {
      liveRef.current = true;
      if (active) setLive(true);
    }).catch(function () {
      if (active) {
        setLive(false);
        AppFeedback.error({ title: t("ارتباط زنده اعلان‌ها برقرار نشد؛ تازه‌سازی کنید.") });
      }
    });
    function onFocus() {
      if (!failed.current && active && !liveRef.current && Date.now() - refreshedAt.current >= 60_000) void refresh();
    }
    window.addEventListener("focus", onFocus);
    return function () {
      active = false;
      liveRef.current = false;
      window.removeEventListener("focus", onFocus);
      void connection.stop();
    };
  }, [session, attempt, refresh]);

  async function mark(id?: number) {
    if (mutationLock.current || !session) return;
    const captured = session;
    mutationLock.current = true;
    setPending(id ?? "all");
    try {
      await markNotificationsRead(id);
      if (captured === agencySessionKey()) await refresh();
    } catch (error) { if (captured === agencySessionKey()) void AppFeedback.httpError(error); }
    finally {
      if (captured === agencySessionKey()) { mutationLock.current = false; setPending(null); }
    }
  }

  function retry() {
    void refresh();
    if (!live) setAttempt(function (value) { return value + 1; });
  }
  return { records: state.session === session ? state.records : [], loading, pending, mark, retry, live, session, failed: failed.current };
}
